import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { UserSelector } from '@/components/shared/UserSelector';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { useLinkStore } from '@/stores/linkStore';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'risk');

const riskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  asset_or_process: z.string().optional(),
  cause: z.string().optional(),
  consequence: z.string().optional(),
  impact: z.number().min(1).max(5),
  likelihood: z.number().min(1).max(5),
  status: z.enum(['identified', 'assessing', 'treating', 'monitoring', 'closed']),
  treatment: z.string().optional(),
  notes: z.string().optional(),
  residual_score: z.number().optional(),
  owner_id: z.string().optional(),
});

type RiskFormData = z.infer<typeof riskSchema>;

interface RiskEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  risk: any;
  onSave: (id: string, data: any) => void;
  isSaving?: boolean;
}

export function RiskEditDialog({ open, onOpenChange, risk, onSave, isSaving }: RiskEditDialogProps) {
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  const { loadLinks, links, bulkCreateLinks } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RiskFormData>({
    resolver: zodResolver(riskSchema),
    defaultValues: {
      impact: 3,
      likelihood: 3,
      status: 'identified',
    },
  });

  useEffect(() => {
    if (risk && open) {
      reset({
        title: risk.title || '',
        asset_or_process: risk.asset_or_process || '',
        cause: risk.cause || '',
        consequence: risk.consequence || '',
        impact: risk.impact || 3,
        likelihood: risk.likelihood || 3,
        status: risk.status || 'identified',
        treatment: risk.treatment || '',
        notes: risk.notes || '',
        residual_score: risk.residual_score || undefined,
        owner_id: risk.owner_id || '',
      });

      // Load existing links for this risk
      loadLinks('risk', risk.id).then(() => {
        const riskLinks = links.filter(l => 
          (l.source_type === 'risk' && l.source_id === risk.id) ||
          (l.target_type === 'risk' && l.target_id === risk.id)
        );
        
        const existingLinks: SelectedEntity[] = riskLinks.map(l => {
          const isSource = l.source_type === 'risk';
          const type = (isSource ? l.target_type : l.source_type) as EntityType;
          const id = isSource ? l.target_id : l.source_id;
          return {
            id,
            type,
            name: resolveEntityName(type, id),
          };
        });
        setSelectedLinks(existingLinks);
      });
    }
  }, [risk, open, reset, loadLinks, links, resolveEntityName]);

  const watchedLikelihood = watch('likelihood');
  const watchedImpact = watch('impact');
  const riskScore = watchedLikelihood * watchedImpact;

  const getRiskLevel = (score: number) => {
    if (score >= 20) return { label: 'Very High', color: 'bg-danger text-danger-foreground' };
    if (score >= 15) return { label: 'High', color: 'bg-orange-500 text-white' };
    if (score >= 10) return { label: 'Medium', color: 'bg-warning text-warning-foreground' };
    if (score >= 5) return { label: 'Low', color: 'bg-success text-success-foreground' };
    return { label: 'Very Low', color: 'bg-muted text-muted-foreground' };
  };

  const riskLevel = getRiskLevel(riskScore);

  const onSubmit = async (data: RiskFormData) => {
    // Handle link updates
    if (selectedLinks.length > 0) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);
      
      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('risk', risk.id, targetType as EntityType, targetIds);
      }
    }
    
    onSave(risk.id, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Risk</DialogTitle>
          <DialogDescription>
            Update risk details and assessment
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Risk Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter risk title"
            />
            {errors.title && (
              <p className="text-sm text-danger mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="asset_or_process">Asset or Process</Label>
            <Input
              id="asset_or_process"
              {...register('asset_or_process')}
              placeholder="What asset or process is at risk?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cause">Cause</Label>
              <Textarea
                id="cause"
                {...register('cause')}
                placeholder="What could cause this risk?"
                className="min-h-[80px]"
              />
            </div>
            <div>
              <Label htmlFor="consequence">Consequence</Label>
              <Textarea
                id="consequence"
                {...register('consequence')}
                placeholder="What would happen if this risk occurred?"
                className="min-h-[80px]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="likelihood">Likelihood (1-5) *</Label>
              <Select
                value={watchedLikelihood?.toString()}
                onValueChange={(value) => setValue('likelihood', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="impact">Impact (1-5) *</Label>
              <Select
                value={watchedImpact?.toString()}
                onValueChange={(value) => setValue('impact', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Risk Score</div>
              <div className="text-2xl font-bold">{riskScore}</div>
            </div>
            <Badge className={riskLevel.color}>{riskLevel.label}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={watch('status')}
                onValueChange={(value: any) => setValue('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="identified">Identified</SelectItem>
                  <SelectItem value="assessing">Assessing</SelectItem>
                  <SelectItem value="treating">Treating</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="treatment">Treatment</Label>
              <Select
                value={watch('treatment') || 'none'}
                onValueChange={(value) => setValue('treatment', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="accept">Accept</SelectItem>
                  <SelectItem value="mitigate">Mitigate</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="avoid">Avoid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="residual_score">Residual Score (after treatment)</Label>
            <Input
              id="residual_score"
              type="number"
              min="0"
              max="25"
              {...register('residual_score', { valueAsNumber: true })}
              placeholder="Optional"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Additional notes and observations"
              className="min-h-[100px]"
            />
          </div>

          <UserSelector
            value={watch('owner_id')}
            onChange={(userId) => setValue('owner_id', userId)}
            label="Risk Owner"
            placeholder="Select owner"
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
